/**
 * Model Audit Mode — shared contract between the audit router
 * (server/routes/modelAudit.ts), the qa-detail-v3 audit panel, and the
 * /model/audit queue page.
 *
 * Purpose: let an admin walk every delivery from one ops day inside the real
 * QA detail page and grade, per field and per side, whether the model's
 * prediction was right — WITHOUT touching QA decisions, agent labels,
 * training queues, or model_measurements. Findings land in the dedicated
 * model_audit_findings table (one row per delivery, upserted).
 *
 * Verdict vocabulary (from Sarah's Aug 16 walk-through examples):
 *   correct     — model value is right
 *   wrong       — model produced a value but it's wrong (OCR misread,
 *                 misclassified drop zone, stale PDR, ...)
 *   missed      — model found nothing, but the info WAS there (in the task
 *                 text for expected, visible in the photos for actual)
 *   not_present — nothing to find: no unit exists / not visible in any
 *                 photo / irrelevant for this delivery
 *
 * Pure data + pure functions only — no DOM, no React, no DB. This file is
 * imported by server vitest tests (see server/__tests__/model-audit-helpers
 * .test.ts), so keep it side-effect free.
 */

import { z } from "zod";

export const AUDIT_FIELDS = ["streetNumber", "unitNumber", "dzeCode"] as const;
export type AuditField = (typeof AUDIT_FIELDS)[number];

export const AUDIT_SIDES = ["expected", "actual"] as const;
export type AuditSide = (typeof AUDIT_SIDES)[number];

export const AUDIT_VERDICTS = ["correct", "wrong", "missed", "not_present"] as const;
export type AuditVerdict = (typeof AUDIT_VERDICTS)[number];

export const AUDIT_FIELD_LABELS: Record<AuditField, string> = {
  streetNumber: "Street #",
  unitNumber: "Unit #",
  dzeCode: "Drop Zone",
};

export const AUDIT_VERDICT_LABELS: Record<AuditVerdict, string> = {
  correct: "Correct",
  wrong: "Wrong",
  missed: "Missed",
  not_present: "N/A",
};

/** Per-side verdict map — all three fields required (seeding guarantees a
 * complete map before the first save, see seedAuditVerdicts). */
export type AuditSideVerdicts = Record<AuditField, AuditVerdict>;
export type AuditVerdicts = Record<AuditSide, AuditSideVerdicts>;

/** Model values at grading time. The pipeline gets re-run/backfilled, so the
 * finding snapshots what the model said when Sarah graded it. */
export type AuditSideSnapshot = Record<AuditField, string | null>;
export type AuditModelSnapshot = Record<AuditSide, AuditSideSnapshot>;

/** Compact per-cell provenance labels ("PDR", "BERT 93%", "scene 85%", ...)
 * captured with each finding so the day summary can split error rates by
 * pipeline stage — a wrong DZ from PDR (stale reference) needs a different
 * fix than one from BERT (model) or the scene classifier (vision). */
export type AuditSources = Record<AuditSide, Record<AuditField, string | null>>;

// ─────────────────────────────────────────────────────────────────────────────
// PUT body schema — strict, mirrors the annotation-review isolation guarantee:
// the endpoint accepts these keys and nothing else, so the audit flow can
// never accidentally carry QA-decision fields.
// ─────────────────────────────────────────────────────────────────────────────

const verdictEnum = z.enum(AUDIT_VERDICTS);

const sideVerdictsSchema = z
  .object({
    streetNumber: verdictEnum,
    unitNumber: verdictEnum,
    dzeCode: verdictEnum,
  })
  .strict();

const sideSnapshotSchema = z
  .object({
    streetNumber: z.string().nullable(),
    unitNumber: z.string().nullable(),
    dzeCode: z.string().nullable(),
  })
  .strict();

export const auditFindingBodySchema = z
  .object({
    verdicts: z
      .object({ expected: sideVerdictsSchema, actual: sideVerdictsSchema })
      .strict(),
    modelSnapshot: z
      .object({ expected: sideSnapshotSchema, actual: sideSnapshotSchema })
      .strict()
      .optional(),
    sources: z
      .object({ expected: sideSnapshotSchema, actual: sideSnapshotSchema })
      .strict()
      .optional(),
    note: z.string().max(4000).nullable().optional(),
  })
  .strict();

export type AuditFindingBody = z.infer<typeof auditFindingBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Seeding — default verdicts so grading a mostly-correct delivery is one
// click (Save & Next), not six.
//   value present → "correct"   (Sarah flips the exceptions)
//   value absent  → "not_present" (flip to "missed" when the info was there)
// ─────────────────────────────────────────────────────────────────────────────

export function seedAuditVerdicts(snapshot: AuditModelSnapshot): AuditVerdicts {
  const seedSide = (side: AuditSideSnapshot): AuditSideVerdicts => {
    const out = {} as AuditSideVerdicts;
    for (const field of AUDIT_FIELDS) {
      const v = side[field];
      out[field] = v != null && String(v).trim() !== "" ? "correct" : "not_present";
    }
    return out;
  };
  return {
    expected: seedSide(snapshot.expected),
    actual: seedSide(snapshot.actual),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Provenance — compact source labels per cell, derived from the delivery's
// expected_model_basis (expected side) and actual_evidence (actual side).
// Shapes observed on live Aug-16 data:
//   basis:    { dzeCode: 'PDR verified record: "DOOR"',
//               streetNumber: 'BERT pipeline [bert] (93.0%)',
//               unitNumber: 'Keyword match in boilerplate destination notes: ...' }
//   evidence: [{ field: "actualDzeCode", decision: "selected",
//                sourceType: "scene", confidence: 85, ... },
//              { field: "yoloStreetNumber", decision: "selected",
//                sourceType: "yolo", ... }]
// Used by the audit panel (display), the PUT body (persistence), and the
// backfill script — keep it pure.
// ─────────────────────────────────────────────────────────────────────────────

function expectedSourceLabel(basis: string | null | undefined): string | null {
  if (!basis) return null;
  const pctMatch = basis.match(/\((\d+(?:\.\d+)?)%\)/);
  const pct = pctMatch ? ` ${Math.round(parseFloat(pctMatch[1]))}%` : "";
  if (/^PDR/i.test(basis)) return "PDR";
  if (/\[bert\]|BERT/i.test(basis)) return `BERT${pct}`;
  if (/keyword/i.test(basis)) return "notes kw";
  if (/onfleet/i.test(basis)) return "onfleet";
  if (/task/i.test(basis)) return "task";
  // Unknown basis phrasing — first word beats losing the signal entirely.
  return basis.split(/[\s:]/)[0].toLowerCase() || null;
}

const ACTUAL_SOURCE_LABELS: Record<string, string> = {
  scene: "scene",
  building: "bldg OCR",
  yolo: "YOLO OCR",
  notes: "notes",
  barcode: "barcode",
};

interface EvidenceEntryLike {
  field?: string;
  decision?: string;
  sourceType?: string;
  confidence?: number;
}

function actualSourceLabel(
  evidence: EvidenceEntryLike[] | null | undefined,
  field: AuditField,
): string | null {
  if (!Array.isArray(evidence)) return null;
  const cap = field.charAt(0).toUpperCase() + field.slice(1);
  // The selected evidence entry for the final value; YOLO shadow entries
  // (yoloStreetNumber / yoloUnitNumber) are the fallback when no direct
  // actualX entry was selected (observed on ~2/3 of Aug-16 street numbers).
  const pick =
    evidence.find((e) => e?.decision === "selected" && e?.field === `actual${cap}`) ??
    evidence.find((e) => e?.decision === "selected" && e?.field === `yolo${cap}`);
  if (!pick?.sourceType) return null;
  const label = ACTUAL_SOURCE_LABELS[pick.sourceType] ?? pick.sourceType;
  const conf = typeof pick.confidence === "number" && pick.confidence > 0 ? ` ${pick.confidence}%` : "";
  return `${label}${conf}`;
}

/** Derives all six source labels from the delivery API payload (camelCase
 * fields from GET /api/qa/deliveries/:id, or a snake→camel mapped DB row). */
export function deriveAuditSources(delivery: {
  expectedModelBasis?: Record<string, string> | null;
  actualEvidence?: EvidenceEntryLike[] | null;
}): AuditSources {
  const basis = delivery.expectedModelBasis ?? null;
  const evidence = delivery.actualEvidence ?? null;
  const expected = {} as Record<AuditField, string | null>;
  const actual = {} as Record<AuditField, string | null>;
  for (const field of AUDIT_FIELDS) {
    expected[field] = expectedSourceLabel(basis?.[field]);
    actual[field] = actualSourceLabel(evidence, field);
  }
  return { expected, actual };
}

/** True when at least one of the six cells is a model error (wrong or
 * missed). Used for the queue page's per-row "N wrong" chip. */
export function countAuditErrors(verdicts: AuditVerdicts): number {
  let n = 0;
  for (const side of AUDIT_SIDES) {
    for (const field of AUDIT_FIELDS) {
      const v = verdicts[side]?.[field];
      if (v === "wrong" || v === "missed") n++;
    }
  }
  return n;
}
